// @ts-nocheck

// Lightning-fast optimized dropdown class
class CustomDropdown {
    constructor(element, options = {}) {
        this.element = element;
        this.options = {
            placeholder: 'Select option',
            searchPlaceholder: 'Search...',
            noResultsText: 'No results found',
            noChoicesText: 'No choices available',
            ...options
        };

        this.isOpen = false;
        this.isDisabled = false;
        this.isLoading = false;
        this.isVisuallyDisabled = false;
        this.selectedValue = null;
        this.selectedLabel = null;
        this.choices = [];
        this.filteredChoices = [];
        this.highlightedIndex = -1;
        this.searchQuery = '';
        this._isInteracting = false;

        // Performance optimizations
        this.virtualStart = 0;
        this.virtualEnd = 50; // Only render 50 items at once
        this.itemHeight = 36; // Fixed item height for virtual scrolling
        this._rafId = null;
        this._filterTimeout = null;

        this.init();
    }

    init() {
        this.createDropdownHTML();
        this.bindEvents();
    }

    createDropdownHTML() {
        const wrapper = document.createElement('div');
        wrapper.className = 'custom-dropdown';
        wrapper.innerHTML = `
            <div class="dropdown-trigger">
                <span class="dropdown-value">${this.options.placeholder}</span>
                <span class="dropdown-arrow"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg></span>
            </div>
            <div class="dropdown-menu">
                <div class="dropdown-search">
                    <input type="text" class="dropdown-search-input" placeholder="${this.options.searchPlaceholder}">
                </div>
                <ul class="dropdown-list"></ul>
                <div class="dropdown-notice" style="display: none;"></div>
            </div>
        `;

        // Replace the original element
        this.element.parentNode.insertBefore(wrapper, this.element);
        this.element.style.display = 'none';

        // Store references
        this.wrapper = wrapper;
        this.trigger = wrapper.querySelector('.dropdown-trigger');
        this.valueEl = wrapper.querySelector('.dropdown-value');
        this.menu = wrapper.querySelector('.dropdown-menu');
        this.searchInput = wrapper.querySelector('.dropdown-search-input');
        this.list = wrapper.querySelector('.dropdown-list');
        this.notice = wrapper.querySelector('.dropdown-notice');

        // Add classes from original element
        if (this.element.disabled) {
            this.disable();
        }
    }

    bindEvents() {
        // Toggle dropdown
        this.trigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!this.isDisabled && !this.isLoading && !this.isVisuallyDisabled) {
                this.toggle();
            }
        });

        // Also allow focus/keyboard interaction on trigger
        this.trigger.setAttribute('tabindex', '0');
        this.trigger.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                if (!this.isDisabled && !this.isLoading && !this.isVisuallyDisabled) {
                    this.open();
                }
            }
        });

        // Optimized search input handling
        this.searchInput.addEventListener('input', (e) => {
            this.searchQuery = e.target.value;
            this.filterChoices(); // Direct call with internal debouncing
        });

        // Keyboard navigation
        this.searchInput.addEventListener('keydown', (e) => {
            this.handleKeydown(e);
        });

        // Add tabindex for accessibility
        this.searchInput.setAttribute('tabindex', '0');

        // Outside click with more precise handling
        this._outsideClickHandler = (e) => {
            // Only close if all conditions are met:
            // 1. Dropdown is open
            // 2. Not just opened (prevent immediate closing)
            // 3. Not currently interacting with dropdown
            // 4. Click target is truly outside the dropdown wrapper
            // 5. Not clicking on another dropdown trigger
            if (this.isOpen && !this._justOpened && !this._isInteracting) {
                const clickedElement = e.target;
                const isOutsideDropdown = !this.wrapper.contains(clickedElement);
                const isNotAnotherDropdownTrigger = !clickedElement.closest('.dropdown-trigger');

                if (isOutsideDropdown && isNotAnotherDropdownTrigger) {
                    this.close();
                }
            }
        };

        // Use mousedown instead of click for better control
        document.addEventListener('mousedown', this._outsideClickHandler, true);

        // Store reference for cleanup
        this._boundOutsideClickHandler = this._outsideClickHandler;

        // Prevent dropdown close on menu click and track interaction
        this.menu.addEventListener('mousedown', (e) => {
            this._isInteracting = true;
            e.stopPropagation();
        });

        this.menu.addEventListener('mouseup', (e) => {
            setTimeout(() => {
                this._isInteracting = false;
            }, 200);
        });

        this.menu.addEventListener('click', (e) => {
            e.stopPropagation();
        });

        // Prevent closing on search input interactions
        this.searchInput.addEventListener('mousedown', (e) => {
            this._isInteracting = true;
            e.stopPropagation();
        });

        this.searchInput.addEventListener('focus', (e) => {
            this._isInteracting = true;
        });

        this.searchInput.addEventListener('blur', (e) => {
            // Don't immediately close on blur - wait a bit
            setTimeout(() => {
                this._isInteracting = false;
            }, 300);
        });

        // Add scroll handling for virtual scrolling
        this.setupVirtualScrolling();
    }

    handleKeydown(e) {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!this.isOpen) {
                    this.open();
                } else {
                    this.highlightNext();
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (this.isOpen) {
                    this.highlightPrevious();
                }
                break;
            case 'Enter':
                e.preventDefault();
                if (this.isOpen) {
                    // Check for exact match first
                    const exactMatch = this.filteredChoices.find(choice => choice.exactMatch);
                    if (exactMatch) {
                        this.selectChoice(exactMatch);
                    } else if (this.highlightedIndex >= 0) {
                        this.selectHighlighted();
                    } else if (this.filteredChoices.length === 1) {
                        // If only one result, select it
                        this.selectChoice(this.filteredChoices[0]);
                    }
                } else {
                    this.open();
                }
                break;
            case 'Escape':
                e.preventDefault();
                this.close();
                break;
            case 'Tab':
                // Allow default tab behavior - don't force close dropdown
                // Let the natural blur/focus cycle handle it
                break;
        }
    }

    highlightNext() {
        const maxIndex = this.filteredChoices.length - 1;
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, maxIndex);
        this.updateHighlight();
    }

    highlightPrevious() {
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        this.updateHighlight();
    }

    updateHighlight() {
        const items = this.list.querySelectorAll('.dropdown-item');
        items.forEach((item, index) => {
            item.classList.toggle('highlighted', index === this.highlightedIndex);
        });

        // Scroll to highlighted item
        if (this.highlightedIndex >= 0 && items[this.highlightedIndex]) {
            items[this.highlightedIndex].scrollIntoView({ block: 'nearest' });
        }
    }

    selectHighlighted() {
        if (this.highlightedIndex >= 0 && this.filteredChoices[this.highlightedIndex]) {
            this.selectChoice(this.filteredChoices[this.highlightedIndex]);
        }
    }

    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        if (this.isDisabled || this.isLoading || this.isVisuallyDisabled) return;

        // Pre-calculate and set menu dimensions to prevent CLS
        this.preventLayoutShift();

        this.isOpen = true;
        this.wrapper.classList.add('is-open');
        this.wrapper?.parentElement?.classList.add('is-open');

        // Ensure choices are rendered when opening
        this.renderChoices();
        this.updateNotice();

        // Prevent immediate closing with longer delay
        this._justOpened = true;
        setTimeout(() => {
            this._justOpened = false;
        }, 300);

        // Enhanced focus handling with multiple strategies
        // this.focusSearchInput();

        this.highlightedIndex = -1;

        // Dispatch custom event
        this.element.dispatchEvent(new CustomEvent('showDropdown'));
    }

    // Prevent CLS by pre-calculating dropdown dimensions
    preventLayoutShift() {
        const menu = this.menu;
        if (!menu) return;

        // Set fixed dimensions before opening to prevent layout shift
        const estimatedHeight = Math.min(this.filteredChoices.length * this.itemHeight + 60, 360); // 60px for search + padding
        menu.style.minHeight = estimatedHeight + 'px';
        menu.style.maxHeight = '360px';
        menu.style.width = this.wrapper.offsetWidth + 'px';

        // Force immediate layout calculation
        menu.style.position = 'absolute';
        menu.style.top = '100%';
        menu.style.left = '0';
        menu.style.zIndex = '1000';
    }

    close() {
        this.isOpen = false;
        this.wrapper.classList.remove('is-open');
        this.wrapper?.parentElement?.classList.remove('is-open');
        this.searchQuery = '';
        this.searchInput.value = '';
        this.searchInput.classList.remove('active');
        this.filterChoices();
        this.highlightedIndex = -1;

        // Dispatch custom event
        this.element.dispatchEvent(new CustomEvent('hideDropdown'));
    }

    // Method to manually force close (compatible with existing code)
    forceClose() {
        this.close();
    }

    // Enhanced focus handling for search input
    focusSearchInput() {
        const focusAttempt = (attempt = 1) => {
            try {
                // Ensure input is visible and interactable
                this.searchInput.style.pointerEvents = 'auto';
                this.searchInput.removeAttribute('readonly');
                this.searchInput.disabled = false;

                // Force visibility
                this.searchInput.style.opacity = '1';
                this.searchInput.style.visibility = 'visible';

                this.searchInput.focus();
                this.searchInput.select();
                this.searchInput.classList.add('active');

                // Verify focus worked
                if (document.activeElement === this.searchInput) {
                    return true; // Success
                }

                // Try alternative focus methods
                if (attempt <= 3) {
                    this.searchInput.click();
                    this.searchInput.focus();

                    if (document.activeElement === this.searchInput) {
                        return true;
                    }
                }

                return false;
            } catch (e) {
                console.warn(`Focus attempt ${attempt} failed:`, e);
                return false;
            }
        };

        // Try immediate focus
        if (focusAttempt(1)) return;

        // Try with slight delay
        requestAnimationFrame(() => {
            if (focusAttempt(2)) return;

            // Final attempt with longer delay
            setTimeout(() => {
                focusAttempt(3);
            }, 50);
        });
    }

    setChoices(choices, valueKey = 'value', labelKey = 'label', clearPrevious = true) {
        if (clearPrevious) {
            this.choices = [];
        }

        // Ultra-fast choice processing (inspired by find-vehicle.js)
        const formattedChoices = [];
        for (let i = 0; i < choices.length; i++) {
            const choice = choices[i];
            formattedChoices.push({
                value: choice[valueKey],
                label: choice[labelKey],
                disabled: choice.disabled || false
            });
        }

        this.choices = clearPrevious ? formattedChoices : this.choices.concat(formattedChoices);
        this.filteredChoices = this.choices;

        // Always render choices immediately to show data availability
        this.renderChoices();
        this.updateNotice();
    }

    clearChoices() {
        this.choices = [];
        this.filteredChoices = [];
        this.renderChoices();
        this.updateNotice();
    }

    renderChoices() {
        // Skip RAF for faster rendering - directly render for immediate response
        this.renderOptimizedChoices();
    }

    renderOptimizedChoices() {
        const totalChoices = this.filteredChoices.length;

        // Always use simple rendering for better performance (inspired by find-vehicle.js)
        // Virtual scrolling adds overhead that slows down interactions
        this.renderAllChoices();
    }

    renderAllChoices() {
        // Ultra-fast rendering with minimal DOM operations (inspired by find-vehicle.js)
        const maxVisible = 100; // Limit for performance
        const choicesToRender = this.filteredChoices.slice(0, maxVisible);

        // Build HTML string for fastest rendering
        let htmlString = '';
        choicesToRender.forEach((choice, index) => {
            const exactMatchClass = choice.exactMatch ? ' exact-match' : '';
            const disabledClass = choice.disabled ? ' disabled' : '';

            htmlString += `<li class="dropdown-item${exactMatchClass}${disabledClass}"
                              data-value="${choice.value}"
                              data-index="${index}"
                              style="height: ${this.itemHeight}px; line-height: ${this.itemHeight}px; padding: 0 15px; cursor: pointer;">
                              ${choice.label}
                           </li>`;
        });

        // Single DOM update - much faster than appendChild loop
        this.list.innerHTML = htmlString;
        this.list.style.height = 'auto';
        this.list.style.maxHeight = '300px';
        this.list.style.overflowY = 'auto';

        // Single event delegation for better performance
        this.setupEventDelegation();

        // Show "more results" indicator if items were limited
        if (this.filteredChoices.length > maxVisible) {
            const moreIndicator = document.createElement('li');
            moreIndicator.className = 'dropdown-more-indicator';
            moreIndicator.style.cssText = 'padding: 10px 15px; color: #666; font-style: italic; border-top: 1px solid #eee;';
            moreIndicator.textContent = `... and ${this.filteredChoices.length - maxVisible} more results`;
            this.list.appendChild(moreIndicator);
        }
    }

    setupEventDelegation() {
        // Remove old listener if exists
        if (this._listClickHandler) {
            this.list.removeEventListener('click', this._listClickHandler);
        }

        this._listClickHandler = (e) => {
            const item = e.target.closest('.dropdown-item');
            if (!item || item.classList.contains('disabled')) return;

            const index = parseInt(item.dataset.index);
            const choice = this.filteredChoices[index];
            if (choice) {
                this.selectChoice(choice);
            }
        };

        this.list.addEventListener('click', this._listClickHandler);
    }


    filterChoices() {
        // Instant filtering without debounce for immediate response (like find-vehicle.js)
        this.performFilter();
    }

    performFilter() {
        const query = this.searchQuery.toLowerCase().trim();

        if (!query) {
            this.filteredChoices = this.choices;
        } else {
            // Lightning-fast filtering with early exit optimization
            const results = [];
            const exactMatches = [];
            const startsWithMatches = [];
            let totalMatches = 0;
            const maxResults = 50; // Lower limit for faster performance

            for (let i = 0; i < this.choices.length && totalMatches < maxResults; i++) {
                const choice = this.choices[i];
                const label = choice.label.toLowerCase();

                if (label === query) {
                    exactMatches.push({ ...choice, exactMatch: true });
                    totalMatches++;
                } else if (label.startsWith(query)) {
                    startsWithMatches.push(choice);
                    totalMatches++;
                } else if (label.includes(query)) {
                    results.push(choice);
                    totalMatches++;
                }
            }

            // Fast concatenation - exact matches first for better UX
            this.filteredChoices = exactMatches.concat(startsWithMatches, results);
        }

        // Immediate rendering without RAF delays
        this.renderChoices();
        this.updateNotice();

        // Auto-highlight first item for keyboard navigation
        this.highlightedIndex = this.filteredChoices.length > 0 ? 0 : -1;
        this.updateHighlight();

        // Ensure scroll is at top
        if (this.list) {
            this.list.scrollTop = 0;
        }
    }

    setupVirtualScrolling() {
        if (this._scrollHandler) {
            this.list.removeEventListener('scroll', this._scrollHandler);
        }

        this._scrollHandler = this.throttle(() => {
            const scrollTop = this.list.scrollTop;
            const containerHeight = this.list.clientHeight;

            // Calculate which items should be visible
            const newStart = Math.floor(scrollTop / this.itemHeight);
            const visibleCount = Math.ceil(containerHeight / this.itemHeight);
            const newEnd = Math.min(newStart + visibleCount + 10, this.filteredChoices.length); // 10 item buffer

            if (newStart !== this.virtualStart || newEnd !== this.virtualEnd) {
                this.virtualStart = Math.max(0, newStart - 5); // 5 item buffer before
                this.virtualEnd = newEnd;
                this.renderVirtualChoices();
            }
        }, 16); // ~60fps

        this.list.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    throttle(func, delay) {
        let timeoutId;
        let lastExecTime = 0;
        return (...args) => {
            const currentTime = Date.now();

            if (currentTime - lastExecTime > delay) {
                func.apply(this, args);
                lastExecTime = currentTime;
            } else {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    func.apply(this, args);
                    lastExecTime = Date.now();
                }, delay - (currentTime - lastExecTime));
            }
        };
    }

    updateNotice() {
        if (this.choices.length === 0) {
            this.notice.textContent = this.options.noChoicesText;
            this.notice.style.display = 'block';
            this.list.style.display = 'none';
        } else if (this.filteredChoices.length === 0 && this.searchQuery) {
            this.notice.textContent = this.options.noResultsText;
            this.notice.style.display = 'block';
            this.list.style.display = 'none';
        } else {
            this.notice.style.display = 'none';
            this.list.style.display = 'block';
        }
    }

    selectChoice(choice, suppressEvents = false) {
        this.selectedValue = choice.value;
        this.selectedLabel = choice.label;

        // Ultra-fast immediate visual feedback (inspired by find-vehicle.js)
        this.valueEl.textContent = choice.label;
        this.wrapper.classList.add('has-value');

        // Update original select element
        this.element.value = choice.value;

        // Immediately close dropdown for faster interaction
        this.close();

        // Dispatch events only if not suppressed (after closing for better performance)
        if (!suppressEvents) {
            // Use setTimeout to ensure DOM is updated before event dispatch
            setTimeout(() => {
                this.element.dispatchEvent(new CustomEvent('addItem', {
                    detail: { value: choice.value, label: choice.label }
                }));
            }, 0);
        }
    }

    setChoiceByValue(value, suppressEvents = false) {
        const choice = this.choices.find(c => c.value == value || c.value === String(value));
        if (choice) {
            this.selectChoice(choice, suppressEvents);
        } else {
            console.warn(`Choice with value "${value}" not found in dropdown choices:`, this.choices);
        }
    }

    removeActiveItems() {
        this.selectedValue = null;
        this.selectedLabel = null;
        this.valueEl.textContent = this.options.placeholder;
        this.wrapper.classList.remove('has-value');
        this.element.value = '';
    }

    enable() {
        this.isDisabled = false;
        this.wrapper.classList.remove('is-disabled');
        this.element.disabled = false;
    }

    disable() {
        this.isDisabled = true;
        this.wrapper.classList.add('is-disabled');
        this.element.disabled = true;
        this.close();
    }

    setLoading(loading) {
        this.isLoading = loading;
        if (loading) {
            this.wrapper.classList.add('is-loading');
            this.disable();
        } else {
            this.wrapper.classList.remove('is-loading');
            this.enable();
        }
    }

    setVisuallyDisabled(disabled) {
        this.isVisuallyDisabled = disabled;
        if (disabled) {
            this.wrapper.classList.add('is-visually-disabled');
            this.wrapper.parentNode.classList.add('is-visually-disabled');
            this.trigger.style.pointerEvents = 'none';
            this.trigger.style.opacity = '0.6';
        } else {
            this.wrapper.classList.remove('is-visually-disabled');
            this.wrapper.parentNode.classList.remove('is-visually-disabled');
            // Only restore interactions if not actually disabled or loading
            if (!this.isDisabled && !this.isLoading) {
                this.trigger.style.pointerEvents = '';
                this.trigger.style.opacity = '';
            }
        }
    }

    destroy() {
        if (this._boundOutsideClickHandler) {
            document.removeEventListener('mousedown', this._boundOutsideClickHandler, true);
        }
        this.wrapper.remove();
        this.element.style.display = '';
    }

    // Compatibility properties for existing code
    get dropdown() {
        return { isActive: this.isOpen };
    }
}

// Shared data manager for all VehicleSearch instances
class VehicleSearchDataManager {
    constructor() {
        this.apiBaseUrl = 'https://api.pcfitment.com/v1';

        // Simple sessionStorage-based caching like find-vehicle.js
        this.cache = {
            years: null,
            makes: new Map(),
            models: new Map()
        };

        // Lightweight state tracking without complex loading states
        this.instances = new Set();

        // Cart attributes for server-side persistence
        this.cartAttributes = {
            year: 'vehicle_year',
            make: 'vehicle_make',
            model: 'vehicle_model',
            makeId: 'vehicle_make_id',
            modelId: 'vehicle_model_id'
        };

        // Simple initialization without async overhead
        this.initCache();
    }

    // Lightning-fast cache initialization using sessionStorage like find-vehicle.js
    initCache() {
        try {
            // Load from sessionStorage immediately (synchronous, fast)
            const cachedYears = sessionStorage.getItem('YMM_YEARS_CACHE');
            if (cachedYears) {
                this.cache.years = JSON.parse(cachedYears);
            }
        } catch (e) {
            // Ignore cache errors, will reload from API
        }
    }

    registerInstance(instance) {
        this.instances.add(instance);
    }

    unregisterInstance(instance) {
        this.instances.delete(instance);
    }

    // Simple notification without complex event system
    notifyInstances(eventType, data) {
        this.instances.forEach(instance => {
            if (instance.handleDataUpdate) {
                instance.handleDataUpdate(eventType, data);
            }
        });
    }

    // Optimized fetch with sessionStorage caching like find-vehicle.js
    async fetchData(endpoint, params = {}) {
        const cacheKey = `${endpoint}_${JSON.stringify(params)}`;
        const sessionKey = `YMM_CACHE_${cacheKey}`;

        // Check sessionStorage first (fast, synchronous)
        try {
            const sessionData = sessionStorage.getItem(sessionKey);
            if (sessionData) {
                const parsed = JSON.parse(sessionData);
                // Use shorter cache time for better responsiveness
                if (Date.now() - parsed.timestamp < 900000) { // 15 minute cache
                    return parsed.data;
                }
            }
        } catch (e) {
            // Continue to API call if cache fails
        }

        try {
            const apiURL = `${this.apiBaseUrl}/${endpoint}${params ? '?' + new URLSearchParams(params).toString() : ''}`;

            // Simple fetch without complex abort controllers
            const response = await fetch(apiURL, {
                headers: { 'Content-Type': 'application/json' },
                mode: 'cors'
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const result = await response.json();
            const data = result?.Response || [];

            // Simple sessionStorage caching
            const cacheData = {
                data,
                timestamp: Date.now()
            };
            sessionStorage.setItem(sessionKey, JSON.stringify(cacheData));

            return data;
        } catch (e) {
            console.error(`API Error: ${e.message}`);
            return [];
        }
    }

    // Fast loading with simple caching like find-vehicle.js
    async loadYears() {
        // Return cached data immediately if available
        if (this.cache.years) {
            this.notifyInstances('years', this.cache.years);
            return this.cache.years;
        }

        try {
            const response = await this.fetchData('allYear');
            if (!response || !Array.isArray(response) || response.length === 0) {
                throw new Error('No years data received');
            }

            // Simple processing without complex sorting
            const yearData = response
                .map(item => ({ value: item.Year, label: item.Year }))
                .sort((a, b) => parseInt(b.value) - parseInt(a.value)); // Simple numeric sort

            this.cache.years = yearData;

            // Cache in sessionStorage for fast access
            sessionStorage.setItem('YMM_YEARS_CACHE', JSON.stringify(yearData));

            this.notifyInstances('years', yearData);
            return yearData;
        } catch (error) {
            console.error('Error loading years:', error);
            return [];
        }
    }

    // Simple make loading without complex state management
    async loadMakes(year) {
        if (!year) return [];

        const cacheKey = year;

        // Return cached data immediately if available
        if (this.cache.makes.has(cacheKey)) {
            const cachedData = this.cache.makes.get(cacheKey);
            this.notifyInstances('makes', { year, data: cachedData });
            return cachedData;
        }

        try {
            const response = await this.fetchData('make', { Year: year });
            if (!response || !Array.isArray(response) || response.length === 0) {
                return { makeData: [], choices: [] };
            }

            // Simple data processing
            const makeData = response.filter(item => item.ID && item.Make);
            const choices = makeData
                .map(item => ({ value: item.ID, label: item.Make }))
                .sort((a, b) => a.label.localeCompare(b.label));

            const cacheData = { makeData, choices };
            this.cache.makes.set(cacheKey, cacheData);
            this.notifyInstances('makes', { year, data: cacheData });
            return cacheData;
        } catch (error) {
            console.error('Error loading makes:', error);
            return { makeData: [], choices: [] };
        }
    }

    // Simple model loading
    async loadModels(year, makeId) {
        if (!year || !makeId) return [];

        const cacheKey = `${year}-${makeId}`;

        // Return cached data immediately if available
        if (this.cache.models.has(cacheKey)) {
            const cachedData = this.cache.models.get(cacheKey);
            this.notifyInstances('models', { year, makeId, data: cachedData });
            return cachedData;
        }

        try {
            const response = await this.fetchData('model', { Year: year, Make: makeId });
            if (!response || !Array.isArray(response) || response.length === 0) {
                return { modelData: [], choices: [] };
            }

            // Simple data processing
            const modelData = response.filter(item => item.ID && item.Model);
            const choices = modelData
                .map(item => ({ value: item.ID, label: item.Model }))
                .sort((a, b) => a.label.localeCompare(b.label));

            const cacheData = { modelData, choices };
            this.cache.models.set(cacheKey, cacheData);
            this.notifyInstances('models', { year, makeId, data: cacheData });
            return cacheData;
        } catch (error) {
            console.error('Error loading models:', error);
            return { modelData: [], choices: [] };
        }
    }

    // Simple cart operations without complex async management
    updateCartAttributes(attributes) {
        // Non-blocking cart update
        if (!attributes || Object.keys(attributes).length === 0) return;

        const updates = {};
        Object.keys(attributes).forEach(key => {
            if (this.cartAttributes[key] && attributes[key]) {
                updates[this.cartAttributes[key]] = attributes[key];
            }
        });

        // Fire and forget - don't block UI
        fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: updates })
        }).catch(error => {
            console.warn('Cart update failed:', error);
        });
    }

    clearVehicleCartAttributes() {
        const updates = {};
        Object.values(this.cartAttributes).forEach(key => {
            updates[key] = '';
        });

        // Fire and forget
        fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: updates })
        }).catch(error => {
            console.warn('Cart clear failed:', error);
        });
    }
}

// Global shared instance
const vehicleSearchDataManager = new VehicleSearchDataManager();

class VehicleSearch extends HTMLElement {
    constructor() {
        super();

        this.year = null;
        this.make = null;
        this.model = null;

        this.makeId = null;
        this.modelId = null;

        // Store the full data for reference
        this.makeData = null;
        this.modelData = null;

        this.selectionReady = false;
        this.persist = this.hasAttribute('persist');
        this.autoRedirect = this.hasAttribute('auto-redirect');
        this.stateKey = this.getAttribute('persist-key') || 'vehicle_selection';
        this.suppressEvents = false;

        // Register with data manager
        vehicleSearchDataManager.registerInstance(this);
    }

    connectedCallback() {
        this.yearEl = this.querySelector('[data-vs-year]');
        this.makeEl = this.querySelector('[data-vs-make]');
        this.modelEl = this.querySelector('[data-vs-model]');
        this.selectedTextEl = this.querySelector('[data-vs-selected-text]');
        this.clearButtonEl = this.querySelector('[data-vs-clear]');
        this.searchButtonEl = this.querySelector('[data-vs-search]');
        if (!this.yearEl || !this.makeEl || !this.modelEl) {
            console.error('Vehicle search selectors are not properly configured.');
            return;
        }
        this.bindEvents();
        this.init();
    }

    disconnectedCallback() {
        // Unregister from data manager when component is removed
        vehicleSearchDataManager.unregisterInstance(this);

        // Clean up custom dropdowns
        if (this.yearChoiceEl) {
            this.yearChoiceEl.destroy();
        }
        if (this.makeChoiceEl) {
            this.makeChoiceEl.destroy();
        }
        if (this.modelChoiceEl) {
            this.modelChoiceEl.destroy();
        }
    }

    // Handle updates from the shared data manager - optimized like find-vehicle.js
    handleDataUpdate(eventType, data) {
        switch (eventType) {
            case 'years':
                this.populateYears(data);
                break;
            case 'makes':
                if (data.year === this.year) {
                    this.makeData = data.data.makeData;
                    this.populateMakes(data.data.makeData);
                }
                break;
            case 'models':
                if (data.year === this.year && data.makeId === this.makeId) {
                    this.modelData = data.data.modelData;
                    this.populateModels(data.data.modelData);
                }
                break;
        }
    }

    bindEvents() {
        if (this.yearEl) {
            this.yearChoiceEl = new CustomDropdown(this.yearEl, {
                placeholder: 'Select Year',
                searchPlaceholder: 'Search Year',
                noResultsText: 'No years found',
                noChoicesText: 'No years available'
            });

            // Close other dropdowns when year dropdown opens
            this.yearEl.addEventListener('showDropdown', () => {
                this.closeOtherDropdowns('year');
            });

            this.yearEl.addEventListener('hideDropdown', () => {
                // Year dropdown closed
            });

            this.yearEl.addEventListener('addItem', async (event) => {
                if (this.suppressEvents) return;

                const { value } = event.detail;
                this.year = value;

                // Direct DOM attribute updates like find-vehicle.js - INSTANT feedback
                this.yearEl.setAttribute('data-placeholder', value);
                this.yearEl.setAttribute('data-selval', value);
                if (this.yearChoiceEl?.valueEl) {
                    this.yearChoiceEl.valueEl.textContent = value;
                }

                // Immediate reset of dependent dropdowns - no delays
                this.make = this.model = null;
                this.makeId = this.modelId = null;
                this.makeData = this.modelData = null;

                // Direct attribute updates for make dropdown
                this.makeEl.setAttribute('data-placeholder', 'Select Make');
                this.makeEl.setAttribute('data-selval', '');
                this.makeChoiceEl.removeActiveItems();
                this.makeChoiceEl.clearChoices();
                this.makeChoiceEl.setVisuallyDisabled(true);

                // Direct attribute updates for model dropdown
                this.modelEl.setAttribute('data-placeholder', 'Select Model');
                this.modelEl.setAttribute('data-selval', '');
                this.modelChoiceEl.removeActiveItems();
                this.modelChoiceEl.clearChoices();
                this.modelChoiceEl.setVisuallyDisabled(true);

                // Immediate UI update and state persistence
                this.updateSelectionStatus();
                this.updateButtonStates(); // Update clear/search button
                this.storeStateSync(); // Use synchronous storage

                // Background loading without blocking UI
                try {
                    await vehicleSearchDataManager.loadMakes(this.year);

                    // Enable make dropdown if data is available
                    if (this.makeData?.length > 0) {
                        this.makeChoiceEl.setVisuallyDisabled(false);
                        // Auto-open with minimal delay for better UX
                        setTimeout(() => {
                            this.makeChoiceEl.open();
                        }, 30);
                    }
                } catch (error) {
                    console.error('Error loading makes:', error);
                }
            });
        }

        if (this.makeEl) {
            this.makeChoiceEl = new CustomDropdown(this.makeEl, {
                placeholder: 'Select Make',
                searchPlaceholder: 'Search Make',
                noResultsText: 'No makes found',
                noChoicesText: 'No makes available'
            });

            // Initially set make dropdown as visually disabled (no data available)
            this.makeChoiceEl.setVisuallyDisabled(true);

            // Close other dropdowns when make dropdown opens
            this.makeEl.addEventListener('showDropdown', () => {
                this.closeOtherDropdowns('make');
            });

            this.makeEl.addEventListener('hideDropdown', () => {
                // Make dropdown closed
            });

            this.makeEl.addEventListener('addItem', async (event) => {
                if (this.suppressEvents) return;

                const { value } = event.detail;

                // Find selected make data immediately
                const selectedMake = this.makeData?.find(item => item.ID === value);
                if (selectedMake) {
                    this.makeId = selectedMake.ID;
                    this.make = selectedMake.Make;

                    // Direct DOM attribute updates like find-vehicle.js
                    this.makeEl.setAttribute('data-placeholder', selectedMake.Make);
                    this.makeEl.setAttribute('data-selval', selectedMake.ID);
                    if (this.makeChoiceEl?.valueEl) {
                        this.makeChoiceEl.valueEl.textContent = selectedMake.Make;
                    }
                }

                // Immediate reset of model dropdown
                this.model = null;
                this.modelId = null;
                this.modelData = null;

                // Direct attribute updates for model dropdown
                this.modelEl.setAttribute('data-placeholder', 'Select Model');
                this.modelEl.setAttribute('data-selval', '');
                this.modelChoiceEl.removeActiveItems();
                this.modelChoiceEl.clearChoices();
                this.modelChoiceEl.setVisuallyDisabled(true);

                // Immediate UI update and state persistence
                this.updateSelectionStatus();
                this.updateButtonStates(); // Update clear/search button
                this.storeStateSync();

                // Background loading
                try {
                    await vehicleSearchDataManager.loadModels(this.year, this.makeId);

                    if (this.modelData?.length > 0) {
                        this.modelChoiceEl.setVisuallyDisabled(false);
                        setTimeout(() => {
                            this.modelChoiceEl.open();
                        }, 30);
                    }
                } catch (error) {
                    console.error('Error loading models:', error);
                }
            });
        }

        if (this.modelEl) {
            this.modelChoiceEl = new CustomDropdown(this.modelEl, {
                placeholder: 'Select Model',
                searchPlaceholder: 'Search Model',
                noResultsText: 'No models found',
                noChoicesText: 'No models available'
            });

            // Initially set model dropdown as visually disabled (no data available)
            this.modelChoiceEl.setVisuallyDisabled(true);

            // Close other dropdowns when model dropdown opens
            this.modelEl.addEventListener('showDropdown', () => {
                this.closeOtherDropdowns('model');
            });

            this.modelEl.addEventListener('hideDropdown', () => {
                // Model dropdown closed
            });

            this.modelEl.addEventListener('addItem', async (event) => {
                if (this.suppressEvents) return;

                const { value } = event.detail;

                // Find selected model data immediately
                const selectedModel = this.modelData?.find(item => item.ID === value);
                if (selectedModel) {
                    this.modelId = selectedModel.ID;
                    this.model = selectedModel.Model;

                    // Direct DOM attribute updates like find-vehicle.js
                    this.modelEl.setAttribute('data-placeholder', selectedModel.Model);
                    this.modelEl.setAttribute('data-selval', selectedModel.ID);
                    if (this.modelChoiceEl?.valueEl) {
                        this.modelChoiceEl.valueEl.textContent = selectedModel.Model;
                    }
                }

                // Close dropdown immediately
                this.modelChoiceEl.forceClose();

                // Immediate UI update and state persistence
                this.updateSelectionStatus();
                this.updateButtonStates(); // Update clear/search button
                this.storeStateSync();

                // Instant selection complete handling
                if (this.selectionReady) {
                    const detail = { year: this.year, make: this.make, model: this.model };
                    this.dispatchEvent(new CustomEvent('vehicle-search:selectionComplete', { detail }));

                    if (this.autoRedirect) {
                        const currentParams = new URLSearchParams(window.location.search);
                        // Clear all existing params
                        for (const key of [...currentParams.keys()]) {
                            currentParams.delete(key);
                        }
                        currentParams.set('year', this.year);
                        currentParams.set('make', this.make);
                        currentParams.set('model', this.model);

                        setTimeout(() => {
                            console.log({ currentParams });
                            window.location.href = `/collections/all?${currentParams}`;
                        }, 500);

                    }
                }
            });
        }

        if (this.clearButtonEl) {
            this.clearButtonEl.addEventListener('click', this.clearAll.bind(this));
        }

        if (this.searchButtonEl) {
            this.searchButtonEl.addEventListener('click', this.handleSearch.bind(this));
        }

        // Header vehicle selector button functionality
        this.vehicleSelectorBtn = this.querySelector('#vehicleSelectorBtn');
        this.vehiclePopup = this.querySelector('#vehiclePopup');
        this.closeBtn = this.querySelector('#closeBtn');

        if (this.vehicleSelectorBtn && this.vehiclePopup) {
            this.vehicleSelectorBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleVehiclePopup();
            });

            if (this.closeBtn) {
                this.closeBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.hideVehiclePopup();
                });
            }

            // // Close popup when clicking outside
            // document.addEventListener('click', (e) => {
            //     if (this.vehiclePopup && this.vehiclePopup.classList.contains('active') &&
            //         !this.contains(e.target) &&
            //         !this.vehiclePopup.contains(e.target) &&
            //         !this.vehicleSelectorBtn.contains(e.target)) {
            //         this.hideVehiclePopup();
            //     }
            // });
        }

        // Initial button state update
        this.updateButtonStates();
    }

    updateButtonStates() {
        // If ANY selection is made, show CLEAR button and hide SEARCH
        const hasAnySelection = Boolean(this.year || this.make || this.model);
        const isComplete = this.selectionReady;


        // Find all search and clear buttons in this instance
        const searchButtons = this.querySelectorAll('[data-vs-search]');
        const clearButtons = this.querySelectorAll('[data-vs-clear]');

        if (hasAnySelection) {
            // Show CLEAR button, hide SEARCH button
            searchButtons.forEach(btn => {
                btn.setAttribute('style', isComplete ? 'display: block !important' : 'display: none !important');
                btn.disabled = isComplete ? false : true;
            });
            clearButtons.forEach(btn => {
                btn.setAttribute('style', 'display: block !important');
            });
        } else {
            // Show SEARCH button (disabled if incomplete), hide CLEAR button
            searchButtons.forEach(btn => {
                btn.setAttribute('style', isComplete ? 'display: block !important' : 'display: none !important');
                btn.disabled = isComplete ? false : true;
                btn.style.opacity = isComplete ? '1' : '0.5';
                btn.style.cursor = isComplete ? 'pointer' : 'not-allowed';
            });
            clearButtons.forEach(btn => {
                btn.setAttribute('style', 'display: none !important');
            });
        }

    }

    updateSelectorsState() {
        let customDropdownElements = document.querySelectorAll('.custom-dropdown.is-disabled');
        if (customDropdownElements) {
            customDropdownElements.forEach((el) => {
                el.classList.remove('is-disabled');
            });
        }
    }

    updateSelectionStatus() {
        const complete = Boolean(this.year && this.make && this.model);
        this.selectionReady = complete;
        this.setAttribute('data-complete', complete);

        // Update ALL vehicle selector buttons
        const vehicleSelectorBtns = this.querySelectorAll('.vehicle-selector-btn, #vehicleSelectorBtn');
        vehicleSelectorBtns.forEach(btn => {
            if (complete) {
                btn.classList.add('vehicle-selected');
            } else {
                btn.classList.remove('vehicle-selected');
            }
        });

        // Update ALL checkmark circles
        const checkmarkCircles = this.querySelectorAll('.checkmark-circle');
        checkmarkCircles.forEach(circle => {
            if (complete) {
                circle.classList.add('visible');
            } else {
                circle.classList.remove('visible');
            }
        });

        // Legacy single element fallback
        if (this.vehicleSelectorBtn) {
            if (complete) {
                this.vehicleSelectorBtn.classList.add('vehicle-selected');
            } else {
                this.vehicleSelectorBtn.classList.remove('vehicle-selected');
            }
        }

        this.updateSelectionText();
        this.updateButtonStates(); // Update button states whenever selection changes
    }

    updateSelectionText() {
        const displayText = this.selectionReady
            ? `${this.year || ''} ${this.make || ''} ${this.model || ''}`.trim()
            : 'SELECT YOUR VEHICLE';

        // Update ALL selected text elements
        const selectedTextElements = this.querySelectorAll('[data-vs-selected-text]');
        selectedTextElements.forEach(el => {
            el.textContent = displayText;
        });

        // Also update by class name as fallback
        const vehicleTextElements = this.querySelectorAll('.vehicle-selector-text');
        vehicleTextElements.forEach(el => {
            el.textContent = displayText;
        });

        // Legacy single element fallback
        if (this.selectedTextEl) {
            this.selectedTextEl.textContent = displayText;
        }
    }

    // Helper method to close other dropdowns
    closeOtherDropdowns(except) {
        if (except !== 'year' && this.yearChoiceEl && this.yearChoiceEl.isOpen) {
            this.yearChoiceEl.close();
        }
        if (except !== 'make' && this.makeChoiceEl && this.makeChoiceEl.isOpen) {
            this.makeChoiceEl.close();
        }
        if (except !== 'model' && this.modelChoiceEl && this.modelChoiceEl.isOpen) {
            this.modelChoiceEl.close();
        }
    }

    // Header popup toggle methods
    toggleVehiclePopup() {
        if (this.vehiclePopup.classList.contains('active')) {
            this.hideVehiclePopup();
        } else {
            this.showVehiclePopup();
        }
    }

    showVehiclePopup() {
        this.vehiclePopup.classList.add('active');
        // Auto-focus on year dropdown if available
        if (this.yearChoiceEl && !this.year) {
            setTimeout(() => {
                this.yearChoiceEl.open();
                // Ensure focus after dropdown opens
                setTimeout(() => {
                    // this.yearChoiceEl.focusSearchInput();
                }, 100);
            }, 100);
        }
    }

    hideVehiclePopup() {
        this.vehiclePopup.classList.remove('active');
        // Close any open dropdowns
        if (this.yearChoiceEl && this.yearChoiceEl.isOpen) {
            this.yearChoiceEl.close();
        }
        if (this.makeChoiceEl && this.makeChoiceEl.isOpen) {
            this.makeChoiceEl.close();
        }
        if (this.modelChoiceEl && this.modelChoiceEl.isOpen) {
            this.modelChoiceEl.close();
        }
    }

    isSelectionComplete() {
        return this.selectionReady;
    }

    // Optimized initialization like find-vehicle.js
    async init() {
        this.suppressEvents = true;

        try {
            // Simple sessionStorage-based state restoration like find-vehicle.js
            const yearValue = sessionStorage.getItem('YMM_YEAR') ||
                new URLSearchParams(window.location.search).get('year');
            const makeValue = sessionStorage.getItem('YMM_MAKE') ||
                new URLSearchParams(window.location.search).get('make');
            const modelValue = sessionStorage.getItem('YMM_MODEL') ||
                new URLSearchParams(window.location.search).get('model');

            // Direct assignment like find-vehicle.js
            this.year = yearValue;
            this.make = makeValue;
            this.model = modelValue;

            // Immediate DOM updates without loading states
            if (this.year && this.yearChoiceEl?.valueEl) {
                this.yearChoiceEl.valueEl.textContent = this.year;
                this.yearChoiceEl.wrapper.classList.add('has-value');
                this.yearEl.setAttribute('data-placeholder', this.year);
                this.yearEl.setAttribute('data-selval', this.year);
            }

            if (this.make && this.makeChoiceEl?.valueEl) {
                this.makeChoiceEl.valueEl.textContent = this.make;
                this.makeChoiceEl.wrapper.classList.add('has-value');
                this.makeEl.setAttribute('data-placeholder', this.make);
            }

            if (this.model && this.modelChoiceEl?.valueEl) {
                this.modelChoiceEl.valueEl.textContent = this.model;
                this.modelChoiceEl.wrapper.classList.add('has-value');
                this.modelEl.setAttribute('data-placeholder', this.model);
            }

            // Load data and restore proper IDs in the background
            const loadingPromises = [];

            // Always load years first
            loadingPromises.push(vehicleSearchDataManager.loadYears());

            if (this.year) {
                loadingPromises.push(vehicleSearchDataManager.loadMakes(this.year));

                if (this.make) {
                    // We need to get the makeId first, then load models
                    loadingPromises.push(
                        vehicleSearchDataManager.loadMakes(this.year).then(makeData => {
                            const makeItem = makeData.makeData?.find(item => item.Make === this.make);
                            if (makeItem) {
                                this.makeId = makeItem.ID;
                                this.makeEl.setAttribute('data-selval', makeItem.ID);

                                if (this.model) {
                                    return vehicleSearchDataManager.loadModels(this.year, this.makeId).then(modelData => {
                                        const modelItem = modelData.modelData?.find(item => item.Model === this.model);
                                        if (modelItem) {
                                            this.modelId = modelItem.ID;
                                            this.modelEl.setAttribute('data-selval', modelItem.ID);
                                        }
                                    });
                                }
                            }
                        })
                    );
                }
            }

            // Wait for all data to load
            await Promise.all(loadingPromises);

            this.updateSelectionStatus();
            this.updateButtonStates(); // Set initial button states
            this.updateSelectorsState();

        } catch (error) {
            console.error('Error during initialization:', error);
        } finally {
            this.suppressEvents = false;

        }

    }    // Lightweight populate methods like find-vehicle.js
    populateYears(data) {
        if (!this.yearChoiceEl) return;

        // Simple population without heavy operations
        this.yearChoiceEl.clearChoices();
        if (data && data.length > 0) {
            this.yearChoiceEl.setChoices(data, 'value', 'label', false);

            // If we have a pre-selected year, set it
            if (this.year) {
                this.yearChoiceEl.setChoiceByValue(this.year, true);
            }
        }
    }

    populateMakes(data) {
        if (!this.makeChoiceEl) return;

        // Store makeData for lookups (data could be raw makeData or choices format)
        this.makeData = Array.isArray(data) && data.length > 0 && data[0].ID ? data : null;

        // Convert to choices format if needed
        let choices = data;
        if (Array.isArray(data) && data.length > 0 && data[0].ID) {
            // Raw makeData format - convert to choices
            choices = data.map(item => ({ value: item.ID, label: item.Make }));
        }

        // Simple population with immediate enable/disable
        this.makeChoiceEl.clearChoices();
        if (choices && choices.length > 0) {
            this.makeChoiceEl.setChoices(choices, 'value', 'label', false);
            this.makeChoiceEl.setVisuallyDisabled(false);

            // If we have a pre-selected make, set it
            if (this.make && this.makeData) {
                const makeItem = this.makeData.find(item => item.Make === this.make);
                if (makeItem) {
                    this.makeId = makeItem.ID;
                    this.makeChoiceEl.setChoiceByValue(makeItem.ID, true);
                }
            }
        } else {
            this.makeChoiceEl.setVisuallyDisabled(true);
        }
    }

    populateModels(data) {
        if (!this.modelChoiceEl) return;

        // Store modelData for lookups (data could be raw modelData or choices format)
        this.modelData = Array.isArray(data) && data.length > 0 && data[0].ID ? data : null;

        // Convert to choices format if needed
        let choices = data;
        if (Array.isArray(data) && data.length > 0 && data[0].ID) {
            // Raw modelData format - convert to choices
            choices = data.map(item => ({ value: item.ID, label: item.Model }));
        }

        // Simple population with immediate enable/disable
        this.modelChoiceEl.clearChoices();
        if (choices && choices.length > 0) {
            this.modelChoiceEl.setChoices(choices, 'value', 'label', false);
            this.modelChoiceEl.setVisuallyDisabled(false);

            // If we have a pre-selected model, set it
            if (this.model && this.modelData) {
                const modelItem = this.modelData.find(item => item.Model === this.model);
                if (modelItem) {
                    this.modelId = modelItem.ID;
                    this.modelChoiceEl.setChoiceByValue(modelItem.ID, true);
                }
            }
        } else {
            this.modelChoiceEl.setVisuallyDisabled(true);
        }
    }

    // Synchronous state storage like find-vehicle.js
    storeStateSync() {
        // Immediate sessionStorage updates like find-vehicle.js
        if (this.year) {
            sessionStorage.setItem('YMM_YEAR', this.year);
        } else {
            sessionStorage.removeItem('YMM_YEAR');
        }

        if (this.make) {
            sessionStorage.setItem('YMM_MAKE', this.make);
        } else {
            sessionStorage.removeItem('YMM_MAKE');
        }

        if (this.model) {
            sessionStorage.setItem('YMM_MODEL', this.model);
        } else {
            sessionStorage.removeItem('YMM_MODEL');
        }

        // Immediate URL update (synchronous)
        if (!this.suppressEvents && (this.year || this.make || this.model)) {
            const currentParams = new URLSearchParams(window.location.search);
            if (this.year) currentParams.set('year', this.year);
            if (this.make) currentParams.set('make', this.make);
            if (this.model) currentParams.set('model', this.model);
            window.history.replaceState({}, '', `${window.location.pathname}?${currentParams}`);
        }

        // Background cart update (non-blocking)
        if (!this.suppressEvents && (this.year || this.make || this.model)) {
            vehicleSearchDataManager.updateCartAttributes({
                year: this.year,
                make: this.make,
                model: this.model,
                makeId: this.makeId,
                modelId: this.modelId
            });
        }
    }

    // Legacy async method for compatibility
    storeState() {
        this.storeStateSync();
    }

    getStoredState() {
        try {
            return JSON.parse(localStorage.getItem(this.stateKey));
        } catch {
            return null;
        }
    }

    applyFilters() {
        if (!this.selectionReady) {
            this.dispatchEvent(new CustomEvent('vehicle-search:widgetError', { detail: { error: 'Incomplete selection' } }));
            return;
        }
        const detail = {
            ...(this.year ? { year: this.year } : {}),
            ...(this.make ? { make: this.make } : {}),
            ...(this.model ? { model: this.model } : {})
        };
        this.dispatchEvent(new CustomEvent('vehicle-search:selectionComplete', { detail }));
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set('year', this.year);
        currentParams.set('make', this.make);
        currentParams.set('model', this.model);
        window.history.replaceState({}, '', `${window.location.pathname}?${currentParams}`);
        if (this.autoRedirect) {
            const currentPath = window.location.pathname;
            const isCollectionPage = /^\/collections\/[^/]+$/.test(currentPath);
            const targetPath = isCollectionPage ? currentPath : '/collections/all';
            window.location.href = `${targetPath}?${currentParams}`;
        }
        this.dispatchEvent(new CustomEvent('vehicle-search:widgetAppliedFilters', { detail }));
    }

    handleSearch() {
        if (!this.selectionReady) {
            // Show an error message or highlight missing selections
            console.warn('Please complete your vehicle selection before searching');
            return;
        }

        const detail = {
            year: this.year,
            make: this.make,
            model: this.model
        };

        this.dispatchEvent(new CustomEvent('vehicle-search:selectionComplete', { detail }));

        // Redirect to collection page with parameters
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set('year', this.year);
        currentParams.set('make', this.make);
        currentParams.set('model', this.model);

        const targetPath = '/collections/all';
        window.location.href = `${targetPath}?${currentParams}`;
    }

    // Optimized clear method like find-vehicle.js
    clearAll() {
        this.suppressEvents = true;

        // Reset internal state
        this.year = this.make = this.model = null;
        this.makeId = this.modelId = null;
        this.makeData = this.modelData = null;
        this.selectionReady = false;

        // Direct DOM attribute updates like find-vehicle.js
        this.yearEl.setAttribute('data-placeholder', 'Select Year');
        this.yearEl.setAttribute('data-selval', '');
        this.makeEl.setAttribute('data-placeholder', 'Select Make');
        this.makeEl.setAttribute('data-selval', '');
        this.modelEl.setAttribute('data-placeholder', 'Select Model');
        this.modelEl.setAttribute('data-selval', '');

        // Clear dropdown selections
        this.yearChoiceEl.removeActiveItems();
        this.makeChoiceEl.removeActiveItems();
        this.makeChoiceEl.clearChoices();
        this.makeChoiceEl.setVisuallyDisabled(true);
        this.modelChoiceEl.removeActiveItems();
        this.modelChoiceEl.clearChoices();
        this.modelChoiceEl.setVisuallyDisabled(true);

        // Clear sessionStorage like find-vehicle.js
        sessionStorage.removeItem('YMM_YEAR');
        sessionStorage.removeItem('YMM_MAKE');
        sessionStorage.removeItem('YMM_MODEL');

        sessionStorage.removeItem('boost_yearSelected');
        sessionStorage.removeItem('boost_makeSelected');
        sessionStorage.removeItem('boost_modelSelected');

        // Update UI
        this.setAttribute('data-complete', 'false');
        this.updateSelectionStatus();

        // Background cleanup (non-blocking)
        vehicleSearchDataManager.clearVehicleCartAttributes();

        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete('year');
        currentParams.delete('make');
        currentParams.delete('model');
        currentParams.delete('q');
        // Dynamically clear all Boost filter parameters to prevent orphaning
        for (const key of [...currentParams.keys()]) {
            if (key.startsWith('pf_')) {
                currentParams.delete(key);
            }
        }
        const currentPath = window.location.pathname;
        const isCollectionPage = /^\/collections\/[^/]+$/.test(currentPath);
        const targetPath = isCollectionPage ? '/collections/all' : currentPath;

        // Simple URL redirect like find-vehicle.js
        window.location.href = `${targetPath}?${currentParams}`;

        this.suppressEvents = false;
        this.dispatchEvent(new CustomEvent('vehicle-search:widgetCleared'));
    }

}
customElements.define('vehicle-search', VehicleSearch);


document.addEventListener('quantity-selector:update', () => {
    let spanIntervalCustomDropdowns = setInterval(async function () {
        const customDropdownElements = document.querySelectorAll('.custom-dropdown.is-disabled');
        if (customDropdownElements.length > 0) {
            clearInterval(spanIntervalCustomDropdowns);
            const vehicleSearchElements = document.querySelectorAll('vehicle-search');
            vehicleSearchElements.forEach(async el => {
                await el.init();
            });
        }
    }, 500);


});